import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Package } from 'lucide-react';
import { PurchaseOrder, CreatePurchaseOrderRequest, Supplier, Product } from '../../types';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { supplierService } from '../../services/supplierService';
import { productService } from '../../services/productService';
import { customToast } from '../../utils/customToast';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../hooks/useCurrency';

interface PurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  purchaseOrder: PurchaseOrder | null;
  mode: 'create' | 'edit';
}

interface POItem {
  product_id: number | null;
  custom_product_name?: string;
  product?: Product;
  quantity: number;
  unit_price: number;
}

const PurchaseOrderModal: React.FC<PurchaseOrderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  purchaseOrder,
  mode
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState<CreatePurchaseOrderRequest>({
    supplier_id: 0,
    purchase_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    notes: '',
    items: []
  });
  const [items, setItems] = useState<POItem[]>([{
    product_id: null,
    quantity: 1,
    unit_price: 0
  }]);

  useEffect(() => {
    if (isOpen) {
      setIsDataLoading(true);
      Promise.all([loadSuppliers(), loadAllProducts()]).then(() => {
        setIsDataLoading(false);
      }).catch(error => {
        console.error('Error loading data:', error);
        setIsDataLoading(false);
      });
      
      if (purchaseOrder) {
        setFormData({
          supplier_id: purchaseOrder.supplier_id,
          purchase_date: purchaseOrder.purchase_date,
          expected_delivery_date: purchaseOrder.expected_delivery_date || '',
          notes: purchaseOrder.notes || '',
          items: []
        });
        if (purchaseOrder.items && purchaseOrder.items.length > 0) {
          setItems(purchaseOrder.items.map(item => ({
            product_id: item.product_id,
            product: item.product,
            quantity: item.quantity,
            unit_price: item.unit_price
          })));
        }
      }
    }
  }, [isOpen, purchaseOrder]);

  const loadSuppliers = async () => {
    try {
      const data = await supplierService.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadAllProducts = async () => {
    try {
      const response = await productService.getProducts({}, 1, 1000);
      if (response.data && Array.isArray(response.data)) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      customToast.error('Please select a supplier');
      return;
    }

    if (items.length === 0 || items.some(item => (!item.product_id && !item.custom_product_name) || item.quantity <= 0 || item.unit_price < 0)) {
      customToast.error('Please add at least one valid item with quantity and price');
      return;
    }

    try {
      setLoading(true);
      
      const requestData = {
        ...formData,
        items: items
          .filter(item => item.product_id !== null)
          .map(item => ({
            product_id: item.product_id as number,
            quantity: item.quantity,
            unit_price: item.unit_price
          }))
      };

      if (mode === 'create') {
        await purchaseOrderService.createPurchaseOrder(requestData);
        customToast.success('Purchase order created successfully!');
      } else if (purchaseOrder?.id) {
        await purchaseOrderService.updatePurchaseOrder({
          ...requestData,
          id: purchaseOrder.id
        });
        customToast.success('Purchase order updated successfully!');
      }
      
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error saving purchase order:', error);
      customToast.error(error.response?.data?.message || 'Failed to save purchase order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (index: number, field: string, value: any) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addItem = () => {
    setItems(prev => [...prev, { product_id: null, quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  if (!isOpen) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'} ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                <Package className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                  {mode === 'create' ? 'Create Purchase Order' : 'Edit Purchase Order'}
                </h2>
                <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {mode === 'create' ? 'Create a new purchase order' : 'Update purchase order details'}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}>
          {isDataLoading ? (
            // Loading Skeleton
            <div className="p-4 sm:p-5 md:p-6 space-y-6">
              {/* Section 1 Skeleton */}
              <div className="space-y-4">
                <div className={`h-6 w-48 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className={`h-4 w-24 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Items Section Skeleton */}
              <div className="space-y-4">
                <div className={`h-6 w-40 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className="space-y-3">
                  {[...Array(3)].map((_, idx) => (
                    <div key={idx} className={`p-4 rounded-lg space-y-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className={`h-4 w-32 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes Section Skeleton */}
              <div className="space-y-4">
                <div className={`h-4 w-20 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className={`h-24 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 sm:p-5 md:p-6 space-y-5">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className={`text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                <Package className="w-5 h-5" />
                <span>Order Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Supplier *</label>
                  <select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600' 
                        : 'bg-white border-slate-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Purchase Date *</label>
                  <input
                    type="date"
                    name="purchase_date"
                    value={formData.purchase_date}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600' 
                        : 'bg-white border-slate-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Expected Delivery Date</label>
                  <input
                    type="date"
                    name="expected_delivery_date"
                    value={formData.expected_delivery_date}
                    onChange={handleChange}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600' 
                        : 'bg-white border-slate-300 text-gray-900'
                    }`}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={2}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation resize-none ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600' 
                        : 'bg-white border-slate-300 text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="Add any additional notes"
                  />
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={`text-base font-medium flex items-center space-x-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  <Package className="w-5 h-5" />
                  <span>Items *</span>
                </h3>
                <button
                  type="button"
                  onClick={addItem}
                  className={`flex items-center space-x-1 px-3 py-1.5 text-sm rounded-lg transition-all ${
                    theme === 'dark'
                      ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900/70'
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Item</span>
                </button>
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <table className={`w-full text-sm ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
                  <thead>
                    <tr className={theme === 'dark' ? 'bg-gray-600' : 'bg-gray-50'}>
                      <th className={`px-3 py-2 text-left font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Product</th>
                      <th className={`px-3 py-2 text-left font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Quantity</th>
                      <th className={`px-3 py-2 text-left font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Unit Price</th>
                      <th className={`px-3 py-2 text-left font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Total</th>
                      <th className={`px-3 py-2 text-center font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Action</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-600' : 'divide-gray-200'}`}>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <select
                            value={item.product_id || ''}
                            onChange={(e) => updateItem(index, 'product_id', e.target.value ? Number(e.target.value) : null)}
                            className={`w-full px-2 py-1 text-xs border rounded transition-colors ${
                              theme === 'dark'
                                ? 'bg-gray-600 border-gray-500 text-white focus:bg-gray-500'
                                : 'bg-white border-slate-300 text-gray-900'
                            }`}
                          >
                            <option value="">Select Product</option>
                            {products.map(product => (
                              <option key={product.id} value={product.id}>
                                {product.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                            className={`w-full px-2 py-1 text-xs border rounded transition-colors ${
                              theme === 'dark'
                                ? 'bg-gray-600 border-gray-500 text-white focus:bg-gray-500'
                                : 'bg-white border-slate-300 text-gray-900'
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', Number(e.target.value))}
                            className={`w-full px-2 py-1 text-xs border rounded transition-colors ${
                              theme === 'dark'
                                ? 'bg-gray-600 border-gray-500 text-white focus:bg-gray-500'
                                : 'bg-white border-slate-300 text-gray-900'
                            }`}
                          />
                        </td>
                        <td className={`px-3 py-2 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                          {formatCurrency(item.quantity * item.unit_price)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            disabled={items.length === 1}
                            className={`p-1 rounded transition-all ${
                              items.length === 1
                                ? `${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} cursor-not-allowed`
                                : theme === 'dark'
                                  ? 'text-red-400 hover:bg-gray-600'
                                  : 'text-red-600 hover:bg-gray-100'
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className={theme === 'dark' ? 'bg-gray-600' : 'bg-gray-50'}>
                    <tr>
                      <td colSpan={3} className={`px-3 py-2 text-right font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                        Total Amount:
                      </td>
                      <td className={`px-3 py-2 font-bold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(calculateTotal())}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </form>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button type="button" onClick={handleClose} disabled={loading} className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}>
              Cancel
            </button>
            <button type="submit" disabled={loading} onClick={handleSubmit} className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 touch-manipulation ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'} disabled:opacity-50 disabled:cursor-not-allowed`}>
              {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              <span>{loading ? 'Saving...' : (mode === 'create' ? 'Create Purchase Order' : 'Update Purchase Order')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PurchaseOrderModal;
